/**
 * Utility functions for generating structured data (JSON-LD)
 */

const SITE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://safarglimpse.com' 
  : 'http://localhost:3000';

/**
 * Generate Organization structured data
 * Enhanced with travel-related properties for better search engine understanding
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Safar Glimpse',
  alternateName: 'SafarGlimpse.com',
  url: SITE_URL,
  logo: `${SITE_URL}/safarglimpse-logo.png`,
  image: `${SITE_URL}/safarglimpse-logo.png`,
  description: 'Safar Glimpse is a modern travel exploration platform offering curated destinations, cultural insights, AI-powered travel recommendations, and travel inspiration. Explore UNESCO World Heritage Sites and famous places worldwide.',
  foundingDate: '2025',
  sameAs: [
    // Add social media links when available
    // 'https://facebook.com/safarglimpse',
    // 'https://instagram.com/safarglimpse',
    // 'https://twitter.com/safarglimpse',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    email: 'contact@safarglimpse.com',
    availableLanguage: ['en'],
  },
  // Additional properties for travel-related content
  knowsAbout: [
    'Travel',
    'Tourism',
    'UNESCO World Heritage Sites',
    'Travel Planning',
    'Cultural Heritage',
    'Travel Destinations',
    'AI Travel Recommendations',
  ],
  areaServed: 'Worldwide',
});

/**
 * Generate TravelAgency structured data (optional - use if you want to be categorized as travel agency)
 * Note: Only use this if you actually book/sell travel services
 */
export const generateTravelAgencySchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'TravelAgency',
  name: 'Safar Glimpse',
  url: SITE_URL,
  image: `${SITE_URL}/safarglimpse-logo.png`,
  logo: `${SITE_URL}/safarglimpse-logo.png`,
  description: 'Safar Glimpse is a modern travel exploration platform offering curated destinations, cultural insights, and travel inspiration. Discover UNESCO World Heritage Sites and famous places worldwide with AI-powered recommendations.',
  sameAs: [
    // Add social media links when available
    // 'https://facebook.com/safarglimpse',
    // 'https://instagram.com/safarglimpse',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    email: 'contact@safarglimpse.com',
    availableLanguage: ['en'],
  },
  areaServed: 'Worldwide',
});

/**
 * Generate WebSite structured data with search action
 */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Safar Glimpse',
  url: SITE_URL,
  description: 'Explore UNESCO World Heritage Sites and famous places worldwide. Interactive platform with 1,000+ sites, games, and beautiful UI.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/places?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

/**
 * Generate Place structured data for heritage sites
 */
export const generatePlaceSchema = (place) => {
  if (!place) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: place.name || 'Famous Place',
    description: place.description || place.historicalSignificance || '',
    url: `${SITE_URL}/places/${place.id}`,
  };

  // Add address if available
  if (place.country || place.city) {
    schema.address = {
      '@type': 'PostalAddress',
      addressCountry: place.country || '',
      addressLocality: place.city || '',
    };
  }

  // Add geo coordinates if available
  if (place.latitude && place.longitude) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: place.latitude,
      longitude: place.longitude,
    };
  }

  // Add image if available
  if (place.imageUrl) {
    schema.image = place.imageUrl;
  }

  // Add additional properties
  if (place.yearOfInscription) {
    schema.foundingDate = place.yearOfInscription.toString();
  }

  if (place.category) {
    schema.category = place.category;
  }

  return schema;
};

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbSchema = (items) => {
  if (!items || items.length === 0) return null;

  const breadcrumbItems = items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };
};

/**
 * Generate ItemList structured data for places listing
 */
export const generateItemListSchema = (places, title = 'Famous Places') => {
  if (!places || places.length === 0) return null;

  const items = places.map((place, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: place.name,
    url: `${SITE_URL}/places/${place.id}`,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    description: `Explore our collection of ${places.length} famous places around the world.`,
    numberOfItems: places.length,
    itemListElement: items.slice(0, 20), // Limit to 20 items for performance
  };
};

/**
 * Generate CollectionPage structured data
 */
export const generateCollectionPageSchema = (places, pageTitle, description) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: pageTitle,
  description: description || `Browse ${places.length} famous places around the world.`,
  url: `${SITE_URL}/places`,
  mainEntity: {
    '@type': 'ItemList',
    numberOfItems: places.length,
    itemListElement: places.slice(0, 20).map((place, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Place',
        name: place.name,
        url: `${SITE_URL}/places/${place.id}`,
      },
    })),
  },
});

/**
 * Generate TouristAttraction structured data for Christmas markets
 */
export const generateChristmasMarketSchema = (market) => {
  if (!market) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    name: `${market.name} Christmas Market`,
    description: market.uniqueFocus || `Experience the magic of ${market.name} Christmas Market`,
    image: market.imageUrl,
    url: `${SITE_URL}/christmas-markets/${market.id}`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: market.city,
      addressCountry: market.country,
    },
    touristType: 'Families, Couples, Solo Travelers',
    isAccessibleForFree: true,
    publicAccess: true,
    availableLanguage: ['en', 'local'],
  };
};

/**
 * Generate Event structured data for Christmas markets (as seasonal events)
 */
export const generateChristmasMarketEventSchema = (market) => {
  if (!market) return null;

  const currentYear = new Date().getFullYear();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: `${market.name} Christmas Market ${currentYear}`,
    description: market.uniqueFocus || `Visit the ${market.name} Christmas Market this holiday season`,
    image: market.imageUrl,
    startDate: `${currentYear}-11-20`,
    endDate: `${currentYear}-12-26`,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: `${market.city} City Center`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: market.city,
        addressCountry: market.country,
      },
    },
    organizer: {
      '@type': 'Organization',
      name: `${market.city} Tourism Board`,
      url: SITE_URL,
    },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/christmas-markets/${market.id}`,
      price: '0',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      validFrom: `${currentYear}-11-01`,
    },
  };
};

/**
 * Generate ItemList for top Christmas markets (for SEO)
 */
export const generateChristmasMarketsListSchema = (markets) => {
  if (!markets || markets.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Top European Christmas Markets',
    description: `Discover the ${markets.length} best Christmas markets in Europe. From traditional German markets to charming Eastern European festivities.`,
    numberOfItems: markets.length,
    itemListElement: markets.slice(0, 10).map((market, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'TouristAttraction',
        name: `${market.name} Christmas Market`,
        description: market.uniqueFocus || `${market.city}, ${market.country}`,
        image: market.imageUrl,
        url: `${SITE_URL}/christmas-markets/${market.id}`,
      },
    })),
  };
};

/**
 * Generate FAQ structured data for Christmas markets
 */
export const generateChristmasMarketsFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'When do European Christmas markets typically open?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most European Christmas markets open in late November (around November 20-25) and run through December 24th or 26th. Some markets, like Strasbourg and Vienna, may start earlier or extend into early January.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are the best Christmas markets in Europe?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The top European Christmas markets include: Prague (Czech Republic), Vienna (Austria), Strasbourg (France), Nuremberg (Germany), Budapest (Hungary), and Cologne (Germany). Each offers unique local traditions, food, and crafts.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do Christmas markets charge admission?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most traditional European Christmas markets are free to enter. You only pay for food, drinks, and items you purchase from vendors. Some specialty events or attractions within markets may have separate admission fees.',
      },
    },
    {
      '@type': 'Question',
      name: 'What food and drinks are typical at Christmas markets?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Common Christmas market foods include mulled wine (Glühwein), roasted chestnuts, gingerbread (Lebkuchen), sausages, pretzels, and regional specialties. Each country and city has its unique offerings, from Austrian Punsch to Polish pierogi.',
      },
    },
  ],
});

/**
 * Generate Trip structured data for AI-generated itineraries
 * This is the primary schema for itinerary pages
 */
export const generateItineraryTripSchema = (itinerary) => {
  if (!itinerary) return null;

  const slug = itinerary.slug || itinerary.id;
  const tripUrl = `${SITE_URL}/itinerary/${slug}`;

  // Parse day-by-day activities — prefer dailyItinerary (modern) with dayByDay fallback
  const rawDayByDay = itinerary.dailyItinerary || itinerary.dayByDay;
  let dayByDayData = [];
  try {
    dayByDayData = typeof rawDayByDay === 'string'
      ? JSON.parse(rawDayByDay)
      : rawDayByDay || [];
  } catch (e) {
    dayByDayData = [];
  }

  // Extract activities from either format:
  //   dayByDay format: day.activities (flat array of strings/objects)
  //   dailyItinerary format: day.morning/afternoon/evening (arrays of activity objects)
  const getActivities = (day) => {
    if (day.activities) return day.activities;
    const blocks = [
      ...(day.morning || []),
      ...(day.afternoon || []),
      ...(day.evening || []),
    ];
    return blocks.map(a => a.activity || a.place || a.name || '').filter(Boolean);
  };

  // Build itinerary items from day-by-day data
  const itineraryItems = dayByDayData.slice(0, itinerary.durationDays || 7).map((day, index) => {
    const activities = getActivities(day);
    return {
      '@type': 'Trip',
      name: day.title || `Day ${index + 1}`,
      description: day.description || (Array.isArray(activities) ? activities.map(a => typeof a === 'string' ? a : (a.activity || a.name || '')).join(', ') : ''),
      itinerary: {
        '@type': 'ItemList',
        itemListElement: activities.map((activity, actIndex) => ({
          '@type': 'ListItem',
          position: actIndex + 1,
          item: {
            '@type': 'Thing',
            name: typeof activity === 'string' ? activity : (activity.activity || activity.name || ''),
          },
        })),
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'Trip',
    name: itinerary.title || `${itinerary.durationDays}-Day ${itinerary.itineraryType || 'Tour'} of ${itinerary.country}`,
    description: itinerary.description || `Experience a ${itinerary.durationDays}-day ${itinerary.itineraryType} journey through ${itinerary.country}`,
    url: tripUrl,
    image: itinerary.imageUrl || `${SITE_URL}/safarglimpse-logo.png`,
    touristType: itinerary.itineraryType || 'Cultural Explorer',
    itinerary: {
      '@type': 'ItemList',
      numberOfItems: itinerary.durationDays || dayByDayData.length,
      itemListElement: itineraryItems,
    },
    offers: {
      '@type': 'Offer',
      url: tripUrl,
      price: itinerary.estimatedCost || '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    provider: {
      '@type': 'Organization',
      name: 'Safar Glimpse',
      url: SITE_URL,
    },
  };
};

/**
 * Generate TravelAction structured data for itineraries
 * Represents the act of planning/booking a trip
 */
export const generateItineraryTravelActionSchema = (itinerary) => {
  if (!itinerary) return null;

  const slug = itinerary.slug || itinerary.id;

  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAction',
    name: `Plan ${itinerary.title}`,
    description: `Plan your ${itinerary.durationDays}-day trip to ${itinerary.country}`,
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/itinerary/${slug}`,
    },
    result: {
      '@type': 'Trip',
      name: itinerary.title,
      description: itinerary.description,
    },
  };
};

/**
 * Generate HowTo structured data for itineraries
 * Shows step-by-step planning guide
 */
export const generateItineraryHowToSchema = (itinerary) => {
  if (!itinerary) return null;

  // Parse day-by-day for steps — prefer dailyItinerary (modern) with dayByDay fallback
  const rawStepData = itinerary.dailyItinerary || itinerary.dayByDay;
  let dayByDayData = [];
  try {
    dayByDayData = typeof rawStepData === 'string'
      ? JSON.parse(rawStepData)
      : rawStepData || [];
  } catch (e) {
    dayByDayData = [];
  }

  // Extract activities from either format
  const getStepActivities = (day) => {
    if (day.activities) return day.activities;
    const blocks = [
      ...(day.morning || []),
      ...(day.afternoon || []),
      ...(day.evening || []),
    ];
    return blocks.map(a => a.activity || a.place || a.name || '').filter(Boolean);
  };

  const steps = dayByDayData.slice(0, itinerary.durationDays || 7).map((day, index) => {
    const activities = getStepActivities(day);
    return {
      '@type': 'HowToStep',
      position: index + 1,
      name: day.title || `Day ${index + 1}`,
      text: day.description || (Array.isArray(activities) ? activities.map(a => typeof a === 'string' ? a : (a.activity || a.name || '')).join('. ') : ''),
      itemListElement: activities.map((activity, actIndex) => ({
        '@type': 'HowToDirection',
        position: actIndex + 1,
        text: typeof activity === 'string' ? activity : (activity.activity || activity.name || ''),
      })),
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to Experience ${itinerary.title}`,
    description: `Step-by-step guide for a ${itinerary.durationDays}-day ${itinerary.itineraryType} trip to ${itinerary.country}`,
    url: `${SITE_URL}/itinerary/${itinerary.slug || itinerary.id}`,
    image: itinerary.imageUrl || `${SITE_URL}/safarglimpse-logo.png`,
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: itinerary.estimatedCost || '0',
    },
    totalTime: `P${itinerary.durationDays || 7}D`,
    step: steps,
    supply: [
      {
        '@type': 'HowToSupply',
        name: 'Valid passport',
      },
      {
        '@type': 'HowToSupply',
        name: 'Travel insurance',
      },
      {
        '@type': 'HowToSupply',
        name: 'Local currency or credit card',
      },
    ],
  };
};

/**
 * Generate BreadcrumbList for itinerary detail pages
 */
export const generateItineraryBreadcrumbSchema = (itinerary) => {
  if (!itinerary) return null;

  const slug = itinerary.slug || itinerary.id;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'AI Itineraries',
        item: `${SITE_URL}/itineraries`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: itinerary.country,
        item: `${SITE_URL}/itineraries?country=${itinerary.country}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: itinerary.title,
        item: `${SITE_URL}/itinerary/${slug}`,
      },
    ],
  };
};

/**
 * Generate ItemList for itineraries listing page
 */
export const generateItinerariesListSchema = (itineraries, country = null) => {
  if (!itineraries || itineraries.length === 0) return null;

  const title = country 
    ? `AI Travel Itineraries for ${country}`
    : 'AI-Generated Travel Itineraries';

  const description = country
    ? `Explore ${itineraries.length} curated AI-generated travel itineraries for ${country}`
    : `Discover ${itineraries.length} AI-powered travel itineraries worldwide`;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    description: description,
    numberOfItems: itineraries.length,
    itemListElement: itineraries.slice(0, 20).map((itinerary, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Trip',
        name: itinerary.title,
        description: itinerary.description,
        url: `${SITE_URL}/itinerary/${itinerary.slug || itinerary.id}`,
        image: itinerary.imageUrl,
      },
    })),
  };
};

/**
 * Generate structured data for guide pages (CollectionPage + ItemList + BreadcrumbList)
 */
export const generateGuidePageSchema = (country, itineraries) => {
  if (!country) return null;

  const schemas = [];

  // CollectionPage
  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${country} Travel Guide`,
    description: `Explore ${(itineraries || []).length} curated travel itineraries for ${country}. Find the best trips, budgets, and travel tips.`,
    url: `${SITE_URL}/guides/${encodeURIComponent(country)}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Travel Guides', item: `${SITE_URL}/guides` },
        { '@type': 'ListItem', position: 3, name: country, item: `${SITE_URL}/guides/${encodeURIComponent(country)}` },
      ],
    },
  });

  // ItemList of itineraries
  if (itineraries && itineraries.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Travel Itineraries for ${country}`,
      numberOfItems: itineraries.length,
      itemListElement: itineraries.slice(0, 20).map((it, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Trip',
          name: it.title,
          description: it.description,
          url: `${SITE_URL}/itineraries/${it.slug || it.id}`,
        },
      })),
    });
  }

  return schemas;
};

